import React from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { Link } from "react-router-dom";

import { PageSeo } from "@/components/seo/PageSeo";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { MaskReveal } from "@/components/motion/MaskReveal";
import { Reveal } from "@/components/motion/Reveal";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { TerminalScreen } from "@/components/intelligence/TerminalScreen";
import { usePrefillArea } from "@/hooks/use-contact-prefill";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { addLocalePrefix } from "@/lib/locale";

const TERMINAL_URL = "https://terminal.frac.io/";

function ExternalPrimary({ label, ariaLabel, testId, size = "md" }) {
  return (
    <Button
      as="a"
      href={TERMINAL_URL}
      target="_blank"
      rel="noopener noreferrer"
      variant="primary-on-dark"
      size={size}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {label}
      <ArrowUpRight className="ml-1 h-4 w-4" strokeWidth={2} aria-hidden />
    </Button>
  );
}

function Chip({ children, testId }) {
  return (
    <span
      className="inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-caps text-eyebrow"
      style={{
        borderColor: "rgba(114, 0, 219, 0.45)",
        background: "rgba(114, 0, 219, 0.08)",
      }}
      data-testid={testId}
    >
      {children}
    </span>
  );
}

function Hero() {
  const { t } = useT("intelligence");
  // Defensive: skip the intelligence prefix when the locale value is empty
  // OR when i18next falls back to the literal key path (its default behaviour
  // for empty-string translations). Applies to any future empty translation
  // of this key too, not just EN.
  const h1IntelligencePrefix = t("hero.h1IntelligencePrefix");
  const showIntelligencePrefix =
    h1IntelligencePrefix && h1IntelligencePrefix !== "hero.h1IntelligencePrefix";
  return (
    <section
      className="container-frac pt-24 pb-8 md:pt-32 md:pb-12"
      data-testid="intelligence-hero"
    >
      <div className="max-w-5xl">
        <Reveal>
          <span className="eyebrow" data-testid="page-eyebrow">
            {t("hero.eyebrow")}
          </span>
        </Reveal>
        <MaskReveal
          as="h1"
          className="mt-6 text-4xl font-bold leading-tight tracking-tight text-ink-primary sm:text-5xl lg:text-6xl"
          data-testid="page-h1"
        >
          {t("hero.h1Prefix")}
          <br />
          {showIntelligencePrefix && h1IntelligencePrefix}
          <span
            style={{
              background: "var(--gradient-brand)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              color: "transparent",
            }}
          >
            {t("hero.h1Highlight")}
          </span>
          {t("hero.h1Suffix")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 max-w-3xl text-lg leading-normal text-ink-secondary">
            {t("hero.body")}
          </p>
        </Reveal>
        <Reveal delay={280}>
          <div className="mt-6" data-testid="intelligence-hero-disclaimer">
            <span
              className="inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium text-ink-tertiary"
              style={{ borderColor: "var(--border-hairline)" }}
            >
              {t("hero.disclaimer")}
            </span>
          </div>
        </Reveal>
        <Reveal delay={380}>
          <div className="mt-10">
            <ExternalPrimary
              label={t("hero.cta")}
              ariaLabel={t("hero.ctaAria")}
              testId="intelligence-cta-hero"
              size="lg"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function JudgementDesk() {
  const { t } = useT("intelligence");
  const capabilities = t("desk.capabilities", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="intelligence-desk"
    >
      <div className="max-w-4xl">
        <div className="flex flex-wrap items-center gap-3">
          <Reveal>
            <span className="eyebrow" data-testid="desk-eyebrow">
              {t("desk.eyebrow")}
            </span>
          </Reveal>
          <Reveal delay={80}>
            <Chip testId="desk-badge">{t("desk.badge")}</Chip>
          </Reveal>
        </div>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
          data-testid="desk-h2"
        >
          {t("desk.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-6 text-lg leading-normal text-ink-secondary">
            {t("desk.lead")}
          </p>
        </Reveal>
        <Reveal delay={280}>
          <p
            className="mt-6 text-sm text-ink-tertiary"
            data-testid="desk-coverage"
          >
            <span className="font-semibold uppercase tracking-caps text-eyebrow">
              {t("desk.coverageLabel")}:
            </span>{" "}
            {t("desk.coverage")}
          </p>
        </Reveal>
      </div>

      <div className="mt-14">
        <Reveal>
          <h3 className="text-xs font-semibold uppercase tracking-caps text-eyebrow">
            {t("desk.subheading")}
          </h3>
        </Reveal>
        <Stagger
          className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2"
          gap={40}
          data-testid="desk-capabilities-grid"
        >
          {capabilities.map((line, i) => (
            <StaggerItem key={i}>
              <Card variant="glass" className="h-full">
                <div className="flex items-start gap-3">
                  <span
                    className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border"
                    style={{
                      borderColor: "rgba(114, 0, 219, 0.45)",
                      background: "rgba(114, 0, 219, 0.12)",
                    }}
                    aria-hidden
                  >
                    <Check className="h-3.5 w-3.5 text-frac-violet" strokeWidth={2.5} />
                  </span>
                  <p className="text-[15px] leading-snug text-ink-primary">{line}</p>
                </div>
              </Card>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal delay={200}>
          <div className="mt-10">
            <a
              href={TERMINAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm font-semibold text-frac-violet transition-transform duration-fast ease-out hover:translate-x-0.5"
              data-testid="desk-inline-cta"
            >
              {t("desk.inlineCta")}
              <ArrowUpRight className="h-4 w-4" strokeWidth={2} aria-hidden />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Gallery({ eyebrow, h2, items, clarifier, testIdPrefix, eagerFirst = false }) {
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid={`intelligence-${testIdPrefix}`}
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{eyebrow}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {h2}
        </MaskReveal>
        {clarifier && (
          <Reveal delay={160}>
            <p className="mt-5 text-sm leading-normal text-ink-tertiary">
              {clarifier}
            </p>
          </Reveal>
        )}
      </div>

      <Stagger
        className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2"
        gap={60}
        data-testid={`${testIdPrefix}-grid`}
      >
        {items.map((item, i) => (
          <StaggerItem key={item.key}>
            <TerminalScreen
              src={item.src}
              alt={item.alt}
              caption={item.caption}
              width={item.width}
              height={item.height}
              eager={eagerFirst && i === 0}
            />
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function Products() {
  const { t } = useT("intelligence");
  const items = t("products.items", { returnObjects: true }) || [];
  const testIds = [
    "rwa-demand-score", "asset-readiness-score", "liquidity-friction-score",
    "sector-playbooks", "investor-persona-maps", "comparable-campaign-benchmarks",
    "contributor-reputation-index", "proof-of-utility-feed",
  ];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="intelligence-products"
    >
      <div className="max-w-4xl">
        <div className="flex flex-wrap items-center gap-3">
          <Reveal>
            <span className="eyebrow" data-testid="products-eyebrow">
              {t("products.eyebrow")}
            </span>
          </Reveal>
          <Reveal delay={80}>
            <Chip testId="products-badge">{t("products.badge")}</Chip>
          </Reveal>
        </div>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("products.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-6 text-lg leading-normal text-ink-secondary">
            {t("products.lead")}
          </p>
        </Reveal>
        <Reveal delay={260}>
          <p
            className="mt-6 text-[11px] font-semibold uppercase tracking-caps text-ink-tertiary"
            data-testid="products-illustrative"
          >
            {t("products.illustrative")}
          </p>
        </Reveal>
      </div>

      <div className="mt-14">
        <Reveal>
          <h3 className="text-xs font-semibold uppercase tracking-caps text-eyebrow">
            {t("products.subheading")}
          </h3>
        </Reveal>
        <Stagger
          className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
          gap={60}
        >
          {items.map((c, i) => (
            <StaggerItem key={i}>
              <Card variant="glass" className="h-full" data-testid={`capability-${testIds[i]}`}>
                <h4 className="text-base font-bold leading-snug text-ink-primary">
                  {c.label}
                </h4>
                <p className="mt-3 text-sm leading-normal text-ink-secondary">
                  {c.body}
                </p>
              </Card>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function Audiences() {
  const { t } = useT("intelligence");
  const items = t("audiences.items", { returnObjects: true }) || [];
  const testIds = [
    "institutional-allocators",
    "sector-focused-funds",
    "enterprise-issuers",
    "family-offices-private-banks",
  ];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="intelligence-audiences"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("audiences.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("audiences.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-6 text-base leading-normal text-ink-secondary">
            {t("audiences.intro")}
          </p>
        </Reveal>
      </div>
      <Stagger className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" gap={70}>
        {items.map((a, i) => (
          <StaggerItem key={i}>
            <Card variant="glass" className="h-full" data-testid={`audience-${testIds[i]}`}>
              <h3 className="text-base font-bold text-ink-primary">
                {a.label}
              </h3>
              <p className="mt-3 text-sm leading-normal text-ink-secondary">
                {a.body}
              </p>
            </Card>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function Access() {
  const { t } = useT("intelligence");
  const locale = useLocale();
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="intelligence-access"
    >
      <div className="mx-auto max-w-3xl text-center">
        <Reveal>
          <span className="eyebrow">{t("access.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold leading-tight tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("access.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("access.body")}
          </p>
        </Reveal>
        <Reveal delay={350}>
          <div className="mt-10">
            <Button
              as={Link}
              to={addLocalePrefix("/contact?area=data_tokenization", locale)}
              variant="primary-on-dark"
              size="lg"
              data-testid="intelligence-cta-access"
            >
              {t("access.cta")}
              <ArrowUpRight className="ml-1 h-4 w-4" strokeWidth={2} aria-hidden />
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function BottomCta() {
  const { t } = useT("intelligence");
  return (
    <section
      className="container-frac pb-24 md:pb-32"
      data-testid="intelligence-bottom-cta"
    >
      <div className="mx-auto max-w-3xl text-center">
        <Reveal>
          <span className="eyebrow" data-testid="bottom-cta-eyebrow">
            {t("bottomCta.eyebrow")}
          </span>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-4 text-lg leading-normal text-ink-secondary">
            {t("bottomCta.body")}
          </p>
        </Reveal>
        <Reveal delay={260}>
          <div className="mt-8">
            <ExternalPrimary
              label={t("bottomCta.cta")}
              ariaLabel={t("hero.ctaAria")}
              testId="intelligence-cta-bottom"
              size="lg"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Intelligence() {
  usePrefillArea("data_tokenization");
  const { t } = useT("intelligence");
  const galleryA = t("galleryA.items", { returnObjects: true }) || [];
  const galleryB = t("galleryB.items", { returnObjects: true }) || [];

  return (
    <>
      <PageSeo
        title={t("seoTitle")}
        description={t("seoDescription")}
        path="/intelligence"
        image="/og/intelligence.png"
      />
      <div data-testid="page-intelligence">
        <Hero />
        <JudgementDesk />
        <Gallery
          eyebrow={t("galleryA.eyebrow")}
          h2={t("galleryA.h2")}
          items={galleryA}
          testIdPrefix="gallery-a"
          eagerFirst
        />
        <Gallery
          eyebrow={t("galleryB.eyebrow")}
          h2={t("galleryB.h2")}
          items={galleryB}
          clarifier={t("galleryB.clarifier")}
          testIdPrefix="gallery-b"
        />
        <Products />
        <Audiences />
        <Access />
        <BottomCta />
      </div>
    </>
  );
}
